'use client';

import { useCallback } from 'react';
import { useRouter } from 'next/navigation';
import { useGetConversationsQuery } from '@/api/features/conversations';
import { reduxLogger } from '@/core/utils/logger';
import { useTour } from '../hooks/useTour';
import { useCompleteTourWithTracking } from '../hooks/useCompleteTourWithTracking';
import { useTourAutoAdvance } from '../hooks/useTourAutoAdvance';
import { TourHighlight } from '../components/TourHighlight';
import { TemplatesWelcomeModal } from './TemplatesWelcomeModal';
import { TemplatesCompleteModal } from './TemplatesCompleteModal';
import { TEMPLATES_TOUR_STEPS } from './types';

/** Count of highlight-type steps (for "Step X of Y" display) */
const HIGHLIGHT_STEPS = TEMPLATES_TOUR_STEPS.filter((s) => s.type === 'highlight');

/**
 * TemplatesTourOverlay - Renders the Templates tour step UI.
 *
 * Only renders when the active tour is 'templates'. On welcome complete,
 * navigates to the Voley Support conversation so the user can try templates.
 */
export function TemplatesTourOverlay() {
  const router = useRouter();
  const { isActive, activeTourId, currentStep, nextStep } = useTour();
  const { completeTourWithTracking } = useCompleteTourWithTracking();

  const isTemplatesTour = activeTourId === 'templates';

  // Auto-advance polling
  useTourAutoAdvance(isTemplatesTour, currentStep, nextStep);

  // Find the Voley Support conversation for navigation
  const { data: searchResults } = useGetConversationsQuery(
    { search: 'Voley Support', limit: 1 },
    { skip: !isTemplatesTour, refetchOnMountOrArgChange: 5 * 60 },
  );
  const voleySupportConversation = searchResults?.items?.[0];

  const handleWelcomeComplete = useCallback(() => {
    if (voleySupportConversation) {
      router.push(`/dashboard?view=all&conversation=${voleySupportConversation.id}`);
      reduxLogger.info('[TemplatesTour] Navigating to Voley Support conversation');
    } else {
      router.push('/dashboard?view=all');
      reduxLogger.warn('[TemplatesTour] Voley Support conversation not found, navigating to dashboard');
    }
    nextStep();
  }, [voleySupportConversation, router, nextStep]);

  const handleFinish = useCallback(() => {
    completeTourWithTracking('templates');
    router.push('/dashboard?view=all');
  }, [completeTourWithTracking, router]);

  const handleHighlightNext = useCallback(() => {
    nextStep();
  }, [nextStep]);

  if (!isTemplatesTour || !isActive || !currentStep) return null;

  // Backdrop visibility — block clicks only for non-interactive, non-auto-advancing highlight steps
  const isHighlightStep = currentStep.type === 'highlight';
  const isClickThrough = !!(
    currentStep.advanceOnNavigate ||
    currentStep.advanceOnElement ||
    currentStep.advanceOnElementRemoved
  );
  const isInteractive = !!currentStep.allowInteraction;
  const showBackdrop = isHighlightStep && !isClickThrough && !isInteractive;

  return (
    <>
      {/* Persistent backdrop */}
      <div
        className="fixed inset-0 transition-opacity duration-200"
        style={{
          zIndex: 199,
          pointerEvents: showBackdrop ? 'auto' : 'none',
          opacity: showBackdrop ? 1 : 0,
        }}
      />

      {/* Step content */}
      {currentStep.type === 'modal' && (
        <>
          {currentStep.id === 'templates_welcome' && <TemplatesWelcomeModal onComplete={handleWelcomeComplete} />}
          {currentStep.id === 'templates_complete' && <TemplatesCompleteModal onFinish={handleFinish} />}
        </>
      )}

      {currentStep.type === 'highlight' &&
        (() => {
          const highlightIndex = HIGHLIGHT_STEPS.findIndex((s) => s.id === currentStep.id);
          return (
            <TourHighlight
              step={currentStep}
              stepNumber={highlightIndex + 1}
              totalSteps={HIGHLIGHT_STEPS.length}
              onNext={handleHighlightNext}
              isLastStep={highlightIndex === HIGHLIGHT_STEPS.length - 1}
            />
          );
        })()}
    </>
  );
}
